import { useCallback } from 'react';
import { View } from 'react-native';

import { useExtractedQuestionsBubbleContext } from '@/components/chat/bubbles/extractedQuestions/ExtractedQuestionsBubbleContext';
import {
  QUESTION_PILL_VIEW_STYLE,
  getQuestionPillColors,
} from '@/components/chat/bubbles/extractedQuestions/questionPillStyle';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getPressProps } from '@/util/pressProps';

interface QuestionChipProps {
  questionIndex: number;
  position: 'left' | 'right';
  /** The recommended case layout's chip colors for the sender's own bubble. Temporary. */
  recommended?: boolean;
}

/**
 * Inline `Q{n}` ordinal rendered after a highlighted source span. Tapping
 * pulses the matching question card below.
 *
 * Implemented as a plain `<View>` containing a `<Text>` rather than a single
 * inline `<Text>`. Nested `<Text>` inside a parent `<Text>` on native uses
 * NSAttributedString attributes for `backgroundColor` and ignores
 * `borderRadius`, so the chip would render as a sharp rectangle. A `<View>`
 * inside a `<Text>` is supported by RN as an inline-block element with full
 * box-model styling — including a `borderRadius` clipped via `overflow:
 * hidden` — so the chip renders as a true pill on iOS, Android, and web.
 */
export function QuestionChip({ questionIndex, position, recommended = false }: QuestionChipProps) {
  const { colors } = useColors();

  const { pulseQuestion } = useExtractedQuestionsBubbleContext();

  const { backgroundColor, textColor } = getQuestionPillColors(colors, position, recommended);

  const onPress = useCallback(() => {
    pulseQuestion(questionIndex);
  }, [pulseQuestion, questionIndex]);

  const pressProps = getPressProps(onPress, getAccessibleHitSlop(24));

  return (
    <View
      className="web:inline-flex web:align-middle"
      style={[QUESTION_PILL_VIEW_STYLE, { backgroundColor }]}>
      <Text
        size="2xs"
        className="font-bold web:cursor-pointer"
        {...pressProps}
        style={{ color: textColor }}
        {...getAccessibilityProps({
          label: `Question ${questionIndex + 1}: jump to card`,
          role: 'button',
          hint: 'Pulses the matching question card',
        })}>
        {`Q${questionIndex + 1}`}
      </Text>
    </View>
  );
}

export { QUESTION_PILL_VIEW_STYLE };
